import { useCallback, useEffect, useRef } from 'react';
import { Typography } from 'app/components/ui';
import { i18n } from 'app/utils/i18n';
import { useUpdateTag } from 'app/api/tag-queries';

interface HiddenValueFieldProps {
  tagId: string;
  documentSuperId: number;
  defaultValue: string;
  delay?: number;
  disabled?: boolean;
}

export const HiddenValueField = ({ tagId, documentSuperId, defaultValue, delay = 500, disabled = false }: HiddenValueFieldProps) => {
  const { mutate: updateTag } = useUpdateTag();
  const pendingRef = useRef<string | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const flush = useCallback(() => {
    clearTimeout(timerRef.current);
    if (pendingRef.current !== null) {
      updateTag({ id: tagId, value: pendingRef.current, documentSuperId });
      pendingRef.current = null;
    }
  }, [tagId, documentSuperId, updateTag]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      pendingRef.current = e.target.value;
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(flush, delay);
    },
    [flush, delay]
  );

  useEffect(() => {
    return () => {
      clearTimeout(timerRef.current);
      flush();
    };
  }, [flush]);

  return (
    <div className="property-row">
      <Typography fontWeight="semibold" className="property-label">
        {i18n('tag.hidden.value')}
      </Typography>
      {disabled ? (
        <Typography variant="body-7" fontWeight="light">
          {defaultValue || '-'}
        </Typography>
      ) : (
        <input type="text" className="form-control form-control-sm" defaultValue={defaultValue} onChange={handleChange} onBlur={flush} />
      )}
    </div>
  );
};
